import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Special, SpecialsParameters, SpecialsService } from '@cia-front-end-apps/shared/specials';
import { catchError, map, take, tap } from 'rxjs/operators';
import { SpecialsResource } from '@buyiq-core/resources/specials.resource';
import { SpecialStorageService } from '@buyiq-core/storage/specials-storage.service';
import { injectQuery } from '@ngneat/query';

@Injectable({
    providedIn: 'root'
})
export class BatchSpecialsService {
    private query = injectQuery();
    private specialsResource = inject(SpecialsResource);
    private specialsService = inject(SpecialsService);
    private specialStorageService = inject(SpecialStorageService);

    getBatchSpecialsQuery(specialParameters: SpecialsParameters) {
        return this.query({
            queryKey: ['batch-specials', specialParameters],
            queryFn: () => this.getBatchSpecials(specialParameters).pipe(
                map((specials) => specials.filter(s => !this.specialsService.isExpired(s)))
            ),
            networkMode: 'always',
        });
    }

    getBatchSpecials(specialsParameters: SpecialsParameters): Observable<Array<Special>> {
        return this.specialsResource.getAll(specialsParameters, true)
            .pipe(
                tap(this.storeSpecials),
                catchError(() => {
                    return this.specialStorageService
                        .getAll()
                        .pipe(map((specialArrays) => specialArrays.flat()));
                })
            );
    }

    getSpecialsQuery(specialParameters: SpecialsParameters) {
        return this.query({
            queryKey: ['batch-item-specials', specialParameters],
            queryFn: () => this.getSpecials(specialParameters),
            networkMode: 'always',
            staleTime: 60000
        });
    }

    getSpecials(specialsParameters: SpecialsParameters): Observable<Array<Special>> {
        return this.specialsResource.getAll(specialsParameters, false)
            .pipe(
                map((specials) => {
                    return this.specialsService
                        .filterBySubscriptions(specials, [], specialsParameters.vendorId)
                        .filter(special => !this.specialsService.isExpired(special))
                        .filter(special => special.vendorId === specialsParameters.vendorId);
                }),
                tap(specials => {
                    this.specialStorageService.setItem(
                        specialsParameters.upcs[0],
                        specials
                    ).pipe(take(1)).subscribe();
                }),
                catchError(() => {
                    return this.specialStorageService.getItem(specialsParameters.upcs[0])
                })
            );
    }

    private storeSpecials = (specials: Array<Special> = []): void => {
        const groupedByUpc = specials.reduce((hash, obj) => {
            if (obj.upc === undefined) {
                return hash;
            }
            return Object.assign(hash, {
                [obj.upc]: (hash[obj.upc] || []).concat(obj),
            });
        }, {});

        for (const upc in groupedByUpc) {
            if (groupedByUpc.hasOwnProperty(upc)) {
                const upcSpecials = groupedByUpc[upc];
                this.specialStorageService
                    .setItem(upc, upcSpecials)
                    .pipe(take(1))
                    .subscribe();
            }
        }
    };
}
